import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { FormField } from '../../../components/app';
import { SharesDialogProps } from '../types';

/** Доли договорные и из размера вкладов не выводятся (см. ADR 0020). */
export function SharesDialog({ isOpen, onOpenChange, founders, onUpdate }: SharesDialogProps) {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setDrafts(Object.fromEntries(founders.map((f) => [f.id, String(f.profit_share)])));
  }, [isOpen, founders]);

  const totalShare = useMemo(
    () =>
      founders.reduce((acc, founder) => {
        const value = parseFloat(drafts[founder.id] ?? String(founder.profit_share));
        return acc + (Number.isNaN(value) ? 0 : value);
      }, 0),
    [drafts, founders],
  );

  const handleSave = async (id: string) => {
    const value = parseFloat(drafts[id]);
    if (Number.isNaN(value) || value < 0 || value > 100) return;
    setSavingId(id);
    try {
      await onUpdate(id, value);
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0">
        <DialogHeader bordered>
          <DialogTitle>Доли в прибыли</DialogTitle>
          <DialogDescription>
            Договорная величина: от размера Вкладов не зависит. Вклады возвращаются каждому полностью и отдельно.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="gap-3">
          {founders.map((founder) => {
            const value = parseFloat(drafts[founder.id] ?? '');
            const invalid = drafts[founder.id] !== undefined && (Number.isNaN(value) || value < 0 || value > 100);
            return (
              <div
                key={founder.id}
                className="flex items-end justify-between gap-4 rounded-xl border border-border-soft bg-surface-alt p-3"
              >
                <FormField label={`${founder.name}, %`} error={invalid ? 'От 0 до 100' : undefined} className="w-36">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    inputMode="decimal"
                    value={drafts[founder.id] ?? ''}
                    onChange={(e) => setDrafts((prev) => ({ ...prev, [founder.id]: e.target.value }))}
                  />
                </FormField>
                <Button
                  size="sm"
                  onClick={() => handleSave(founder.id)}
                  disabled={savingId === founder.id || invalid || drafts[founder.id] === String(founder.profit_share)}
                >
                  {savingId === founder.id && <Loader2 className="animate-spin" />}
                  Сохранить
                </Button>
              </div>
            );
          })}

          {Math.abs(totalShare - 100) > 0.01 && (
            <p role="status" className="m-0 flex items-center gap-1.5 text-[13px] font-semibold text-warn">
              <AlertTriangle className="size-3.5" aria-hidden />
              Сумма долей — {totalShare.toFixed(2)}%, а должна быть 100%
            </p>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
